import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Shield,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  Search,
  Calendar,
  RefreshCw,
  LogOut,
  CheckCircle,
  XCircle,
  AlertCircle,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extend Modal Component
function ExtendModal({ isOpen, onClose, customer, onConfirm, isLoading }) {
  const [days, setDays] = useState(7);
  
  if (!isOpen || !customer) return null;
  
  // Determine if user is on trial or has active subscription
  const isTrialUser = customer.subscription_status === 'trial' || 
                      (!customer.subscription_end && customer.trial_end);
  
  // Get the current expiry date - use trial_end for trial users, subscription_end for paid users
  const getExpiryDate = () => {
    if (isTrialUser && customer.trial_end) {
      return new Date(customer.trial_end);
    } else if (customer.subscription_end) {
      return new Date(customer.subscription_end);
    }
    return new Date(); // Fallback to today if no expiry set
  };
  
  const currentExpiry = getExpiryDate();
  const newExpiry = new Date(currentExpiry);
  newExpiry.setDate(newExpiry.getDate() + days);
  
  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            Extend {isTrialUser ? 'Free Trial' : 'Subscription'}
          </h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Customer Info */}
        <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
          <p className="text-white font-medium">{customer.name}</p>
          <p className="text-gray-400 text-sm">{customer.email}</p>
          <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${isTrialUser ? 'bg-blue-600/30 text-blue-300' : 'bg-green-600/30 text-green-300'}`}>
            {isTrialUser ? '🎁 Trial User' : '✅ Paid Subscriber'}
          </span>
        </div>
        
        {/* Days Input */}
        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2">
            Days to add
          </label>
          <Input
            type="number"
            value={days}
            onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 0))}
            min="1"
            max="365"
            className="bg-gray-700 border-gray-600 text-white text-center text-lg font-semibold"
          />
        </div>
        
        {/* Quick Options */}
        <div className="mb-4">
          <label className="block text-gray-400 text-xs mb-2">Quick options</label>
          <div className="flex gap-2">
            {[7, 14, 30, 60].map((d) => (
              <Button
                key={d}
                type="button"
                variant={days === d ? "default" : "outline"}
                size="sm"
                onClick={() => setDays(d)}
                className={days === d 
                  ? "bg-orange-600 hover:bg-orange-700 text-white flex-1" 
                  : "border-gray-600 text-gray-300 hover:bg-gray-700 flex-1"
                }
              >
                {d} days
              </Button>
            ))}
          </div>
        </div>
        
        {/* Date Preview */}
        <div className="mb-6 p-3 bg-gray-700/30 rounded-lg border border-gray-600">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">Current expiry:</span>
            <span className="text-gray-300">{formatDate(currentExpiry)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">New expiry:</span>
            <span className="text-green-400 font-medium">{formatDate(newExpiry)}</span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(days)}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            disabled={isLoading}
          >
            {isLoading ? 'Extending...' : `Extend by ${days} days`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Reset Password Modal Component
function ResetPasswordModal({ isOpen, onClose, customer, onConfirm, isLoading, result }) {
  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Reset Password</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Customer Info */}
        <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
          <p className="text-white font-medium">{customer.name}</p>
          <p className="text-gray-400 text-sm">{customer.email}</p>
        </div>
        
        {!result ? (
          <>
            {/* Warning */}
            <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-700 rounded-lg">
              <p className="text-yellow-300 text-sm">
                ⚠️ This will generate a new password. The user's current password will stop working immediately.
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={onConfirm}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Success - Show new password */}
            <div className="mb-4 p-4 bg-green-900/30 border border-green-700 rounded-lg">
              <p className="text-green-300 text-sm mb-2">✅ Password reset successfully!</p>
              <p className="text-gray-400 text-xs mb-2">New temporary password:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-900 px-3 py-2 rounded text-green-400 font-mono text-sm select-all">
                  {result.temp_password}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(result.temp_password);
                    alert('Password copied to clipboard!');
                  }}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Copy
                </Button>
              </div>
            </div>
            
            <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
              <p className="text-blue-300 text-sm">
                📋 Share this password with the user securely. They should change it after logging in.
              </p>
            </div>
            
            {/* Close button */}
            <Button
              onClick={onClose}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white"
            >
              Done
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// Activate Subscription Modal Component
function ActivateSubscriptionModal({ isOpen, onClose, customer, onConfirm, isLoading }) {
  const [selectedPlan, setSelectedPlan] = useState('starter');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [activateSubscription, setActivateSubscription] = useState(true);
  
  if (!isOpen || !customer) return null;
  
  const plans = {
    starter: { name: 'Starter', clients: 50, plans: 100, priceMonthly: '749', priceYearly: '6,499' },
    professional: { name: 'Professional', clients: 100, plans: 250, priceMonthly: '1,499', priceYearly: '12,999' },
    growth: { name: 'Growth', clients: 200, plans: 600, priceMonthly: '2,999', priceYearly: '25,999' }
  };
  
  const selectedPlanInfo = plans[selectedPlan];
  const price = billingCycle === 'yearly' ? selectedPlanInfo.priceYearly : selectedPlanInfo.priceMonthly;
  const duration = billingCycle === 'yearly' ? 365 : 30;
  
  const getExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + duration);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  
  const currentStatus = customer.subscription_status || 'unknown';
  const currentPlan = customer.plan_tier || 'none';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Activate Subscription</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Customer Info */}
        <div className="mb-4 p-3 bg-gray-700/50 rounded-lg">
          <p className="text-white font-medium">{customer.name}</p>
          <p className="text-gray-400 text-sm">{customer.email}</p>
          <p className="text-gray-500 text-xs mt-1">
            Current: {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)} 
            {currentPlan !== 'none' && currentPlan !== 'free_trial' && ` (${currentPlan})`}
          </p>
        </div>
        
        {/* Plan Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">Select Plan</label>
          <div className="space-y-2">
            {Object.entries(plans).map(([key, plan]) => (
              <label 
                key={key}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border transition-colors ${
                  selectedPlan === key 
                    ? 'border-blue-500 bg-blue-900/20' 
                    : 'border-gray-600 hover:border-gray-500 bg-gray-700/30'
                }`}
              >
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="plan"
                    value={key}
                    checked={selectedPlan === key}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                    selectedPlan === key ? 'border-blue-500' : 'border-gray-500'
                  }`}>
                    {selectedPlan === key && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                  <span className="text-white font-medium">{plan.name}</span>
                </div>
                <span className="text-gray-400 text-sm">{plan.clients} clients</span>
              </label>
            ))}
          </div>
        </div>
        
        {/* Billing Cycle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">Billing Cycle</label>
          <div className="flex gap-2">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                  : 'border-gray-600 text-gray-400 hover:border-gray-500'
              }`}
            >
              Monthly (30 days)
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                  : 'border-gray-600 text-gray-400 hover:border-gray-500'
              }`}
            >
              Yearly (365 days)
            </button>
          </div>
        </div>
        
        {/* Activate Subscription Toggle */}
        <div className="mb-4 p-3 bg-gray-700/30 rounded-lg">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-white text-sm font-medium">Activate subscription</p>
              <p className="text-gray-400 text-xs">Set status to active with expiry date</p>
            </div>
            <button
              onClick={() => setActivateSubscription(!activateSubscription)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                activateSubscription ? 'bg-blue-600' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  activateSubscription ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>
        
        {/* Summary */}
        <div className="mb-4 p-3 bg-green-900/20 border border-green-700/50 rounded-lg">
          <p className="text-green-400 text-sm font-medium mb-2">Summary</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Plan:</span>
              <span className="text-white">{selectedPlanInfo.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Limits:</span>
              <span className="text-white">{selectedPlanInfo.clients} clients, {selectedPlanInfo.plans} plans/mo</span>
            </div>
            {activateSubscription && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white">{duration} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Expires:</span>
                  <span className="text-white">{getExpiryDate()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-white">₹{price}</span>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(selectedPlan, billingCycle, activateSubscription)}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            disabled={isLoading}
          >
            {isLoading ? 'Activating...' : activateSubscription ? 'Activate Plan' : 'Change Plan'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('signup_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [extendModal, setExtendModal] = useState({ isOpen: false, customer: null });
  const [resetPasswordModal, setResetPasswordModal] = useState({ isOpen: false, customer: null, result: null });
  const [activateModal, setActivateModal] = useState({ isOpen: false, customer: null });
  const customersPerPage = 10;

  useEffect(() => {
    // Check if admin is logged in
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${adminToken}` };

      const [customersRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/customers`, { headers }),
        axios.get(`${API}/admin/analytics`, { headers })
      ]);

      setCustomers(customersRes.data.customers);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExtendSubscription = async (userId, customerName, days = 30) => {
    setActionLoading(userId);
    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/extend-subscription`,
        { user_id: userId, days },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      alert(`✅ Subscription extended successfully!\n\n${customerName}'s subscription has been extended by ${days} days.`);
      setExtendModal({ isOpen: false, customer: null });
      fetchData();
    } catch (error) {
      alert('❌ Failed to extend subscription. Please try again.');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const openExtendModal = (customer) => {
    setExtendModal({ isOpen: true, customer });
  };

  const closeExtendModal = () => {
    setExtendModal({ isOpen: false, customer: null });
  };

  const openResetPasswordModal = (customer) => {
    setResetPasswordModal({ isOpen: true, customer, result: null });
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModal({ isOpen: false, customer: null, result: null });
  };

  const handleResetPassword = async (userId) => {
    setActionLoading(userId);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/admin/reset-password`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      setResetPasswordModal(prev => ({ ...prev, result: response.data }));
    } catch (error) {
      alert('❌ Failed to reset password. Please try again.');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangePlan = (customer) => {
    setActivateModal({ isOpen: true, customer });
  };

  const handleActivatePlan = async (planTier, billingCycle, activateSubscription) => {
    const customer = activateModal.customer;
    if (!customer) return;

    setActionLoading(customer.id);
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API}/admin/change-plan`,
        { 
          user_id: customer.id, 
          plan_tier: planTier,
          billing_cycle: billingCycle,
          activate_subscription: activateSubscription
        },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      const actionType = activateSubscription ? 'activated' : 'changed';
      const planNames = { starter: 'Starter', professional: 'Professional', growth: 'Growth' };
      
      alert(`✅ Plan ${actionType} successfully!\n\n${customer.name} is now on ${planNames[planTier]} plan.${
        activateSubscription ? `\n\nSubscription expires: ${new Date(response.data.subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''
      }`);
      
      setActivateModal({ isOpen: false, customer: null });
      fetchData();
    } catch (error) {
      alert('❌ Failed to change plan. Please try again.');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (userId, customerName, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'expired' : 'active';
    const action = newStatus === 'active' ? 'enable' : 'disable';
    
    const message = `${action.charAt(0).toUpperCase() + action.slice(1)} account for ${customerName}?\n\nCurrent status: ${currentStatus.toUpperCase()}\nNew status: ${newStatus.toUpperCase()}\n\n${newStatus === 'active' ? '✅ User will regain full access' : '⚠️ User will lose access to create clients/plans'}`;
    
    if (!window.confirm(message)) return;

    setActionLoading(userId);
    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/toggle-status`,
        { user_id: userId, status: newStatus },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      alert(`✅ Account ${action}d successfully!\n\n${customerName}'s status is now: ${newStatus.toUpperCase()}`);
      fetchData();
    } catch (error) {
      alert('❌ Failed to change status. Please try again.');
      console.error(error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  // Filtering
  const filteredCustomers = customers.filter(customer => {
    // Search filter
    const matchesSearch = customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || customer.subscription_status === statusFilter;
    
    // User type filter (advisor vs consumer)
    const userType = customer.role === 'advisor' ? 'advisor' : 'consumer';
    const matchesUserType = userTypeFilter === 'all' || userType === userTypeFilter;
    
    // Plan filter logic
    let matchesPlan;
    if (planFilter === 'all') {
      matchesPlan = true; // Show all customers
    } else if (planFilter === 'free_trial') {
      // "Free Trial" filter shows all non-paying users (trial, expired, or no status)
      matchesPlan = customer.subscription_status !== 'active';
    } else {
      // Specific paid plan filters (starter, professional, etc.) - show only active customers with that plan
      matchesPlan = customer.plan_tier === planFilter && customer.subscription_status === 'active';
    }
    
    return matchesSearch && matchesStatus && matchesPlan && matchesUserType;
  });

  // Sorting
  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'expiry_date':
        const aDate = a.subscription_end || a.trial_end || '';
        const bDate = b.subscription_end || b.trial_end || '';
        comparison = aDate.localeCompare(bDate);
        break;
      case 'signup_date':
        comparison = (a.created_at || '').localeCompare(b.created_at || '');
        break;
      case 'plan':
        comparison = (a.plan_tier || '').localeCompare(b.plan_tier || '');
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Pagination
  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const paginatedCustomers = sortedCustomers.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, planFilter, userTypeFilter, sortBy]);

  const getUserTypeBadge = (role) => {
    const isAdvisor = role === 'advisor';
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isAdvisor 
          ? 'bg-purple-900/20 text-purple-400 border border-purple-800' 
          : 'bg-teal-900/20 text-teal-400 border border-teal-800'
      }`}>
        {isAdvisor ? '👔 Advisor' : '👤 Consumer'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-900/20 text-green-400 border-green-800',
      trial: 'bg-blue-900/20 text-blue-400 border-blue-800',
      expired: 'bg-red-900/20 text-red-400 border-red-800'
    };

    const icons = {
      active: <CheckCircle className="w-4 h-4" />,
      trial: <Clock className="w-4 h-4" />,
      expired: <XCircle className="w-4 h-4" />
    };

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.trial}`}>
        {icons[status]}
        <span className="ml-1">{status?.toUpperCase()}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-orange-500" />
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-gray-400 text-xs md:text-sm">FinAI SaaS Management</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={fetchData}
              className="border-gray-600 text-gray-300 flex-1 md:flex-none"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Refresh</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="border-gray-600 text-gray-300 flex-1 md:flex-none"
              size="sm"
            >
              <LogOut className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-3 md:mb-6">
          <Card className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-green-800 p-3 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-400" />
              <span className="text-xl md:text-2xl font-bold text-white">
                {analytics?.active_users || 0}
              </span>
            </div>
            <p className="text-gray-300 text-xs md:text-sm font-medium">Active Subscriptions</p>
            <p className="text-xs text-green-400 mt-1">
              Paying customers
            </p>
          </Card>

          <Card className="bg-gradient-to-br from-blue-900/20 to-indigo-900/20 border-blue-800 p-3 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-6 h-6 md:w-8 md:h-8 text-blue-400" />
              <span className="text-xl md:text-2xl font-bold text-white">
                {analytics?.non_paying_users || 0}
              </span>
            </div>
            <p className="text-gray-300 text-xs md:text-sm font-medium">Trial/Free Users</p>
            <p className="text-xs text-blue-400 mt-1">
              {analytics?.trial_users || 0} trial • {analytics?.expired_users || 0} expired
              {(analytics?.non_paying_users - (analytics?.trial_users || 0) - (analytics?.expired_users || 0)) > 0 && (
                <span> • {analytics?.non_paying_users - (analytics?.trial_users || 0) - (analytics?.expired_users || 0)} other</span>
              )}
            </p>
          </Card>

          <Card className="bg-gradient-to-br from-orange-900/20 to-red-900/20 border-orange-800 p-3 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-6 h-6 md:w-8 md:h-8 text-orange-400" />
              <span className="text-lg md:text-2xl font-bold text-white">
                ₹{Math.round(analytics?.mrr || 0).toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-gray-300 text-xs md:text-sm font-medium">Monthly Revenue</p>
            <p className="text-xs text-orange-400 mt-1">
              {analytics?.active_users || 0} subscriptions
            </p>
          </Card>

          <Card className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border-purple-800 p-3 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-6 h-6 md:w-8 md:h-8 text-purple-400" />
              <span className="text-lg md:text-2xl font-bold text-white">
                ₹{Math.round(analytics?.total_revenue || 0).toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-gray-300 text-xs md:text-sm font-medium">Total Revenue</p>
            <p className="text-xs text-purple-400 mt-1">
              Lifetime earnings
            </p>
          </Card>
        </div>

        {/* Secondary Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6">
          <Card className="bg-gray-900/40 border-gray-700 p-3 md:p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm text-gray-400">Conversion Rate</span>
              <span className="text-sm md:text-base font-semibold text-white">
                {analytics?.conversion_rate || 0}%
              </span>
            </div>
          </Card>

          <Card className="bg-gray-900/40 border-gray-700 p-3 md:p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm text-gray-400">ARPU</span>
              <span className="text-sm md:text-base font-semibold text-white">
                ₹{Math.round(analytics?.arpu || 0).toLocaleString('en-IN')}
              </span>
            </div>
          </Card>

          <Card className="bg-gray-900/40 border-gray-700 p-3 md:p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm text-gray-400">Expiring Soon</span>
              <span className="text-sm md:text-base font-semibold text-yellow-400">
                {analytics?.expiring_soon || 0}
              </span>
            </div>
          </Card>

          <Card className="bg-gray-900/40 border-gray-700 p-3 md:p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm text-gray-400">Total Customers</span>
              <span className="text-sm md:text-base font-semibold text-white">
                {analytics?.total_users || 0}
              </span>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-cyan-900/30 to-teal-900/30 border-cyan-800 p-3 md:p-4 col-span-2 lg:col-span-1">
            <div className="flex flex-col space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-xs md:text-sm text-cyan-300 font-medium">AI Usage</span>
                <span className="text-sm md:text-base font-bold text-white">
                  {analytics?.total_ai_queries || 0}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span>Est. Cost</span>
                <span className="text-cyan-400">₹{(analytics?.estimated_ai_cost || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Advisors: {analytics?.advisor_ai_queries || 0}</span>
                <span>Consumers: {analytics?.consumer_ai_queries || 0}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Customer List */}
      <div className="max-w-7xl mx-auto">
        <Card className="bg-gray-800 border-gray-700 p-4 md:p-6">
          <div className="flex flex-col gap-4 mb-6">
            {/* Title and Search */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-white">All Customers</h2>
                <p className="text-gray-400 text-sm">
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedCustomers.length)} of {sortedCustomers.length}
                  {customers.length !== sortedCustomers.length && ` (filtered from ${customers.length})`}
                </p>
              </div>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            {/* Filters and Sort */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {/* User Type Filter */}
              <select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Users</option>
                <option value="advisor">Advisors Only</option>
                <option value="consumer">Consumers Only</option>
              </select>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="trial">Trial Only</option>
                <option value="expired">Expired Only</option>
              </select>

              {/* Plan Filter */}
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Plans</option>
                <option value="free_trial">Free Trial</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>

              {/* Sort By */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="signup_date">Signup Date</option>
                <option value="expiry_date">Expiry Date</option>
                <option value="name">Name</option>
                <option value="plan">Plan</option>
              </select>

              {/* Sort Order */}
              <Button
                variant="outline"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="border-gray-600 text-gray-300 hover:bg-gray-700 text-sm px-3"
                size="sm"
                title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
                <span className="hidden md:inline ml-1">{sortOrder === 'asc' ? 'Asc' : 'Desc'}</span>
              </Button>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="pb-3 text-gray-400 font-medium">Customer</th>
                  <th className="pb-3 text-gray-400 font-medium">Type</th>
                  <th className="pb-3 text-gray-400 font-medium">Plan</th>
                  <th className="pb-3 text-gray-400 font-medium">Status</th>
                  <th className="pb-3 text-gray-400 font-medium">Expires</th>
                  <th className="pb-3 text-gray-400 font-medium">Usage</th>
                  <th className="pb-3 text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCustomers.map((customer) => (
                  <tr key={customer.id} className="border-b border-gray-700/50">
                    <td className="py-4">
                      <div>
                        <p className="text-white font-medium">{customer.name}</p>
                        <p className="text-gray-400 text-sm">{customer.email}</p>
                      </div>
                    </td>
                    <td className="py-4">
                      {getUserTypeBadge(customer.role)}
                    </td>
                    <td className="py-4">
                      <span className="text-white capitalize">{customer.plan_tier || 'N/A'}</span>
                      {customer.role === 'advisor' && (
                        <p className="text-gray-400 text-xs">{customer.clients_count || 0}/{customer.client_limit || 0} clients</p>
                      )}
                    </td>
                    <td className="py-4">
                      {getStatusBadge(customer.subscription_status)}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <div>
                          {customer.subscription_end ? (
                            <>
                              <p className="text-white text-sm">
                                {new Date(customer.subscription_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </p>
                              <p className="text-gray-400 text-xs">
                                {customer.days_remaining > 0 ? `${customer.days_remaining}d left` : 'Expired'}
                              </p>
                            </>
                          ) : customer.trial_end ? (
                            <>
                              <p className="text-white text-sm">
                                {new Date(customer.trial_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </p>
                              <p className="text-gray-400 text-xs">Trial</p>
                            </>
                          ) : (
                            <p className="text-gray-400 text-sm">N/A</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 text-gray-300 text-sm">
                      {customer.role === 'advisor' ? (
                        <div className="space-y-1">
                          <span className="block">{customer.plans_this_month || 0} plans</span>
                          <span className="block text-purple-400">{customer.ai_queries || 0} AI queries</span>
                        </div>
                      ) : (
                        <span>{customer.ai_queries || 0} AI queries</span>
                      )}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openExtendModal(customer)}
                          disabled={actionLoading === customer.id}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                        >
                          Extend
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleChangePlan(customer)}
                          disabled={actionLoading === customer.id}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                        >
                          Change
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openResetPasswordModal(customer)}
                          disabled={actionLoading === customer.id}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                        >
                          Reset Pwd
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleStatus(customer.id, customer.name, customer.subscription_status)}
                          disabled={actionLoading === customer.id}
                          className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                        >
                          {customer.subscription_status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {paginatedCustomers.map((customer) => (
              <Card key={customer.id} className="bg-gray-700 border-gray-600 p-4">
                <div className="space-y-3">
                  {/* Customer Info + Type Badge */}
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-white font-medium">{customer.name}</p>
                      <p className="text-gray-400 text-sm">{customer.email}</p>
                    </div>
                    {getUserTypeBadge(customer.role)}
                  </div>

                  {/* Status and Plan */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-xs">Plan</p>
                      <p className="text-white capitalize">{customer.plan_tier || 'N/A'}</p>
                      {customer.role === 'advisor' && (
                        <p className="text-gray-400 text-xs">{customer.clients_count || 0}/{customer.client_limit || 0} clients</p>
                      )}
                    </div>
                    <div className="text-right">
                      {getStatusBadge(customer.subscription_status)}
                    </div>
                  </div>

                  {/* Usage */}
                  <div className="text-sm text-gray-300">
                    {customer.role === 'advisor' ? (
                      <div className="space-y-1">
                        <span className="block">📊 {customer.plans_this_month || 0} plans this month</span>
                        <span className="block text-purple-400">🤖 {customer.ai_queries || 0} AI queries</span>
                      </div>
                    ) : (
                      <span>🤖 {customer.ai_queries || 0} AI queries</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-4 gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExtendModal(customer)}
                      disabled={actionLoading === customer.id}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                    >
                      Extend
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleChangePlan(customer)}
                      disabled={actionLoading === customer.id}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                    >
                      Change
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openResetPasswordModal(customer)}
                      disabled={actionLoading === customer.id}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                    >
                      Reset Pwd
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(customer.id, customer.name, customer.subscription_status)}
                      disabled={actionLoading === customer.id}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                    >
                      {customer.subscription_status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {filteredCustomers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400">No customers found</p>
            </div>
          )}

          {/* Pagination Controls */}
          {filteredCustomers.length > customersPerPage && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="border-gray-600 text-gray-300 disabled:opacity-50"
                size="sm"
              >
                ← Previous
              </Button>

              <div className="flex items-center space-x-2">
                <span className="text-gray-400 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
              </div>

              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="border-gray-600 text-gray-300 disabled:opacity-50"
                size="sm"
              >
                Next →
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* Extend Subscription Modal */}
      <ExtendModal
        isOpen={extendModal.isOpen}
        onClose={closeExtendModal}
        customer={extendModal.customer}
        onConfirm={(days) => handleExtendSubscription(extendModal.customer.id, extendModal.customer.name, days)}
        isLoading={actionLoading === extendModal.customer?.id}
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={resetPasswordModal.isOpen}
        onClose={closeResetPasswordModal}
        customer={resetPasswordModal.customer}
        onConfirm={() => handleResetPassword(resetPasswordModal.customer.id)}
        isLoading={actionLoading === resetPasswordModal.customer?.id}
        result={resetPasswordModal.result}
      />

      {/* Activate Subscription Modal */}
      <ActivateSubscriptionModal
        isOpen={activateModal.isOpen}
        onClose={() => setActivateModal({ isOpen: false, customer: null })}
        customer={activateModal.customer}
        onConfirm={handleActivatePlan}
        isLoading={actionLoading === activateModal.customer?.id}
      />
    </div>
  );
}
